import React, { FC, useEffect } from 'react';
import { StaticContext } from 'react-router';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Asset, BoltIcon, ArrowForwardIcon, Button } from '@clearcover/atlas';

import { reportEvent } from 'Shared/analytics';
import { Body } from 'QuoteApp/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';

import { Confetti } from './Confetti';
import { PolicyManagement } from './PolicyManagement';
import { StepItemModal } from './StepItem';
import styles from './styles.module.css';
import { PostBindSteps } from './PostBindSteps';

type DiscountVerification = {
  name: Disclosures | string;
  heading: string;
  modal?: StepItemModal;
  content?: string;
};

export type VerificationData = {
  [key: string]: DiscountVerification & {
    error: boolean;
    loading: boolean;
    disclosureContent: null | string;
  };
};

export type CongratsAgentHistoryLocationState =
  | undefined
  | {
      pointsEarned?: number;
      didEstimatePointsFail?: boolean;
      policyId?: string;
    };

export const CongratsAgent: FC<RouteComponentProps<
  {
    policyNumber: string;
  },
  StaticContext,
  CongratsAgentHistoryLocationState
>> = ({ match, location }) => {
  const { details, rewardsEnrollmentResponse, rate } = useAppContext();

  const rewardPoints = location.state?.pointsEarned;

  const showRewardsContent =
    rewardsEnrollmentResponse?.isEnrolled && !!rewardPoints && rewardPoints > 0;
  const showRewardsError =
    rewardsEnrollmentResponse?.isEnrolled &&
    location.state?.didEstimatePointsFail;

  useEffect(() => {
    reportEvent(
      !showRewardsError && showRewardsContent
        ? 'CongratsAgent:PointsEarned:display'
        : 'CongratsAgent:PointsError:display'
    );
  }, [
    rewardPoints,
    rewardsEnrollmentResponse?.isEnrolled,
    showRewardsError,
    showRewardsContent,
  ]);

  return (
    <Body data-testid="congrats-screen">
      <div className={styles.confetti}>
        <Confetti />
      </div>
      <div className={styles.page}>
        <div className={styles.content}>
          <div className={styles.details}>
            <h1 className={styles.detailsHeading}>Payment complete!</h1>
            <div className={styles.detailsLabel}>
              It's time to submit the paperwork.
            </div>
            <div className={styles.policyInfo}>
              {match?.params.policyNumber && (
                <div className={styles.policyDetail}>
                  <span className="fs-exclude">
                    Policy Number: {match.params.policyNumber}
                  </span>
                </div>
              )}
              <div className={styles.policyDetail}>
                Effective: {details.effectiveDate.value}
                {details.terminationDate.value
                  ? ` - ${details.terminationDate.value}`
                  : ''}
              </div>
            </div>
            {showRewardsError && (
              <Link
                to="/rewards"
                onClick={() =>
                  reportEvent(
                    'CongratsAgent:PointsEarned:RewardsCenterNavigateErrorIcon'
                  )
                }
              >
                <div className={styles.rewardsContainer}>
                  <Asset
                    icon={BoltIcon}
                    height={28}
                    width={28}
                    description="Bolt icon"
                    className={styles.rewardIcon}
                  />
                  <div className={styles.rewardsError}>
                    We're still calculating your point earnings. Check out the
                    Rewards Center for updates!
                  </div>
                  <Asset
                    icon={ArrowForwardIcon}
                    height={28}
                    width={28}
                    description="Arrow Forward icon"
                    className={styles.rewardsCenterArrowIcon}
                  />
                </div>
              </Link>
            )}
            {showRewardsContent && (
              <Link
                to="/rewards"
                onClick={() =>
                  reportEvent(
                    'CongratsAgent:PointsEarned:RewardsCenterNavigateIcon'
                  )
                }
              >
                <div className={styles.rewardsContainer}>
                  <Asset
                    icon={BoltIcon}
                    height={28}
                    width={28}
                    description="Bolt icon"
                    className={styles.rewardIcon}
                  />
                  <div className={styles.rewardsEarned}>
                    You earned
                    <span
                      className={
                        rewardPoints
                          ? styles.pointsTextBolded
                          : styles.pointsText
                      }
                    >
                      {rewardPoints ? ` ${rewardPoints} points ` : ' points '}
                    </span>
                    with this policy sale!
                  </div>
                  <Asset
                    icon={ArrowForwardIcon}
                    height={28}
                    width={28}
                    description="Arrow Forward icon"
                    className={styles.rewardsCenterArrowIcon}
                  />
                </div>
              </Link>
            )}
          </div>
          <div className={styles.listContent}>
            <div className={styles.stepsHeading}>
              Important! Here's how to finalize the policy.
            </div>
            <div className={styles.stepsSubHeading}>
              This is what your customer needs to do next. (We'll also email
              them instructions.)
            </div>
            <PostBindSteps />
          </div>
        </div>

        {location.state?.policyId &&
          rate?.paymentDetails.attributes.paymentProvider === 'adyen' && (
            <PolicyManagement policyId={location.state.policyId} />
          )}
        <div className={styles.returnButtonWrapper}>
          {rewardsEnrollmentResponse?.isEnrolled && (
            <Link
              to="/rewards"
              onClick={() =>
                reportEvent(
                  'CongratsAgent:PointsEarned:RewardsCenterNavigateButton'
                )
              }
            >
              <Button
                data-testid="confetti-open-rewards-center"
                variant="secondary"
                className={styles.rewardsCenterButton}
              >
                Open Reward Center
              </Button>
            </Link>
          )}
          <Link to="/quotes">
            <Button
              data-testid="confetti-return-to-portal"
              variant="primary"
              className={styles.returnToPortalButton}
            >
              Return to Portal
            </Button>
          </Link>
        </div>
      </div>
    </Body>
  );
};
